/** Searchable picker of the client's bank or money app for a wire transfer, with logos. */
import { useState } from 'react';
import { cx } from '../../../components/ui';
import { BANK_LOGOS } from './bankLogos';
import { banksFor, type Bank } from './banks';

export const OTHER_BANK = '__other';

const luminance = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
};
const initials = (b: Bank) =>
  b.short ??
  b.name
    .split(/\s+/)
    .filter((w) => !/^(bank|of|the|de|uk)$/i.test(w))[0]!
    .slice(0, 1)
    .toUpperCase();

export function BankLogo({ bank, size = 32 }: { bank: Bank; size?: number }) {
  const logo = BANK_LOGOS[bank.logo ?? bank.name];
  const box = { width: size, height: size };
  const cls = 'grid shrink-0 place-items-center overflow-hidden rounded-[9px]';
  if (logo && 'svg' in logo) return <span className={cx(cls, 'bg-white [&>svg]:size-full')} style={box} aria-hidden dangerouslySetInnerHTML={{ __html: logo.svg }} />;
  if (logo)
    return (
      <span className={cls} style={{ ...box, padding: Math.round(size * 0.22), background: logo.color, color: luminance(logo.color) > 0.6 ? '#163300' : '#fff' }} aria-hidden>
        <svg viewBox="0 0 24 24" className="size-full" fill="currentColor">
          <path d={logo.path} />
        </svg>
      </span>
    );
  return (
    <span className={cx(cls, 'text-[11px] leading-none font-bold tracking-tight')} style={{ ...box, background: bank.color, color: luminance(bank.color) > 0.6 ? '#111' : '#fff' }} aria-hidden>
      {initials(bank)}
    </span>
  );
}

export function BankPicker({ country, countryName, value, other, onChange, onOther }: { country: string; countryName: string; value: string; other: string; onChange: (name: string) => void; onOther: (name: string) => void }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const { local, emi } = banksFor(country);
  const match = (b: Bank) => !q || b.name.toLowerCase().includes(q);
  const tile = (b: Bank) => (
    <button
      key={b.name}
      type="button"
      aria-pressed={value === b.name}
      onClick={() => onChange(b.name)}
      className={cx(
        'flex min-w-0 items-center gap-2.5 rounded-[14px] border bg-card px-2.5 py-2 text-left text-[14px] leading-tight font-medium',
        value === b.name ? 'border-accent bg-accent-bg ring-1 ring-accent ring-inset' : 'border-line-strong hover:border-fg/40',
      )}
    >
      <BankLogo bank={b} />
      <span className="min-w-0 break-words">{b.name}</span>
    </button>
  );
  const group = (title: string, list: Bank[]) =>
    list.length > 0 && (
      <>
        <span className="num col-span-full pt-2 text-[11px] font-medium tracking-[.07em] text-muted uppercase first:pt-0">
          {title} · {list.length}
        </span>
        {list.map(tile)}
      </>
    );

  return (
    <div className="flex flex-col gap-2.5">
      <input className="field" placeholder="Search banks and money apps, e.g. Revolut, Monzo, Wise" aria-label="Search banks" value={query} onChange={(e) => setQuery(e.target.value)} />
      <div className="grid max-h-[380px] grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2 overflow-y-auto p-0.5">
        {group(`Banks in ${countryName}`, local.filter(match))}
        {group('Digital banks and e-money (EMI)', emi.filter(match))}
        <span className="col-span-full pt-2 text-[11px] font-medium tracking-[.07em] text-muted uppercase">Not here?</span>
        <button
          type="button"
          aria-pressed={value === OTHER_BANK}
          onClick={() => onChange(OTHER_BANK)}
          className={cx(
            'flex items-center gap-2.5 rounded-[14px] border bg-card px-2.5 py-2 text-left text-[14px] font-medium',
            value === OTHER_BANK ? 'border-accent bg-accent-bg ring-1 ring-accent ring-inset' : 'border-line-strong',
          )}
        >
          <span className="grid size-8 shrink-0 place-items-center rounded-[9px] bg-track text-[18px] text-muted">+</span>
          My bank isn't listed
        </button>
      </div>
      {value === OTHER_BANK && <input className="field" placeholder="Name of your bank" aria-label="Name of your bank" maxLength={120} value={other} onChange={(e) => onOther(e.target.value)} />}
    </div>
  );
}
